import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { getFormattedDateForCalendar } from '../../utils/appHelper';
import Moment from 'moment';
import './Calendar.css';
import { NavigateBeforeOutlined, NavigateNextOutlined } from '@mui/icons-material';
import { addOpacity } from '../../utils/appHelper';

let monthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MonthView = React.forwardRef((props, ref) => {
    const weekLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const weekShortLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const theme = useTheme();
    

    const handleOnCellClick = (date) => {
        props.onCellClick(date);
    };

    function renderHeaderCell(weekLabel) {
        return (
            <Paper key={weekLabel} style={{
                flex: 1,
                textAlign: 'right',
                padding: '8px',
                boxSizing: 'border-box',
                minWidth: 0,
                borderRadius: '0px',
                boxShadow: `0px 0px 1px -1px ${addOpacity(theme.palette.text.secondary, 0.4)}, 0px 0px 1px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}, 0px 0px 1px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}`
            }}>
                <Typography variant="subtitle2">{weekLabel}</Typography>
            </Paper>
        );
    }

    function renderDayCell(item) {
        let cellJSX = <span>&nbsp;</span>;
        let date = undefined;
        let time = undefined;

        if (props.dayNumbering[0] && props.dayNumbering[0][item] !== null && props.dayNumbering[0][item] !== undefined) {
            date = new Date(props.year, props.month, props.dayNumbering[0][item]);
            cellJSX = props.cellRenderer(date);
            time = date.getTime();
        }

        return (
            <Paper
                key={item}
                style={{
                    flex: 1,
                    overflow: 'hidden',
                    wordBreak: 'break-all',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    borderRadius: '0px',
                    boxShadow: `0px 0px 1px -1px ${addOpacity(theme.palette.text.secondary, 0.4)}, 0px 0px 1px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}, 0px 0px 1px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}`
                }}
                onClick={() => handleOnCellClick(date)}>
                <Box style={{
                    paddingLeft: '2px',
                    paddingRight: '2px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                    height: '100%',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: (time && time === props.selectedCell) ? '#3f51b5' : 'transparent',
                    backgroundColor: getHighlightClass(date),
                    boxSizing: 'border-box'
                }}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Box sx={{justifyContent: 'center', display: 'contents', backgroundColor: 'red'}}>
                            {cellJSX}
                        </Box>
                        <Box sx={{justifyContent: 'center'}}>
                            <Box style={{
                                width: '25px',
                                height: '25px',
                                borderRadius: '50%',
                                textAlign: 'center',
                                border: (props.dayNumbering[1] === props.dayNumbering[0][item]) ? '1px solid' : 'none'
                            }}>
                                <Typography variant="subtitle1" style={{
                                    fontSize: '0.85rem',
                                    lineHeight: '1.5'
                                }} align={(props.dayNumbering[1] === props.dayNumbering[0][item]) ? 'center' : "center"}>
                                    {(props.dayNumbering[0] && props.dayNumbering[0][item]) ? props.dayNumbering[0][item] : <span>&nbsp;</span>}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>
        );
    }

    const getHighlightClass = (date) => {
        let retVal = '';
        if (date && props.highlightDays && props.highlightDays.length > 0) {
            let result = props.highlightDays.filter(x => getFormattedDateForCalendar(x) === getFormattedDateForCalendar(date));
            if (result && result.length > 0) {
                retVal = '#e3f2fd'; // Light blue background for highlight
            }
        }
        return retVal;
    };

    return (
        <div ref={ref} style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', paddingBottom: '1px' }}>
            <Box style={{ marginTop: '16px', marginBottom: '16px', display: 'none' }} className='printContent'>
                <Grid container alignItems='center' justifyContent='flex-end'>
                    <Typography variant="subtitle1" component='span'>{monthLabels[props.month]}, {props.year}</Typography>
                </Grid>
            </Box>

            <Paper style={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '1px',
                boxShadow: `0px -1px 0px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}, 1px 0px 0px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}, 0px 1px 0px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}, -1px 0px 0px 0px ${addOpacity(theme.palette.text.secondary, 0.4)}`,
                width: '100%',
                overflow: 'hidden',
                boxSizing: 'border-box'
            }} variant="elevation" elevation={0}>
                <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'stretch', width: '100%', boxSizing: 'border-box' }}>
                    {(props.weekLabelSize === 'short') ?
                        weekShortLabels.map(shortLabel => renderHeaderCell(shortLabel)) :
                        weekLabels.map(longLabel => renderHeaderCell(longLabel))
                    }
                </Box>

                {[0, 1, 2, 3, 4].map(row => (
                    <Box key={row} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'stretch', width: '100%', boxSizing: 'border-box' }}>
                        {[...Array(7).keys()].map(col => renderDayCell(row * 7 + col))}
                    </Box>
                ))}
                {
                    (props.dayNumbering[0] && props.dayNumbering[0][35]) &&
                    <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'stretch', width: '100%', boxSizing: 'border-box' }}>
                        {[35, 36, 37, 38, 39, 40, 41].map(item => {
                            return renderDayCell(item);
                        })}
                    </Box>
                }
            </Paper>
        </div>
    );
});

const Calendar = (props) => {

    const calendarRef = React.useRef();

    const date = new Date();

    const [month, setMonth] = useState(date.getMonth());
    const [year, setYear] = useState(date.getFullYear());
    const [selectedCell, setSelectedCell] = useState(undefined);
    const [selectedDate, handleDateChange] = useState(new Date());


    useEffect(() => {
        if ((props.month !== null || props.month !== undefined) && props.year) {
            setMonth(props.month);
            setYear(props.year);
            let date = Moment(new Date(props.year, props.month));
            handleDateChange(date);
        }
    }, [props.month, props.year]);

    useEffect(() => {
        setSelectedCell(props.selectedCell);
    }, [props.selectedCell]);

    function daysInMonth(month, year) {
        return 32 - new Date(year, month, 32).getDate();
    }

    function getCurrentMonth() {
        let d = new Date();
        return d.getMonth();
    }

    function getCurrentYear() {
        let d = new Date();
        return d.getFullYear();
    }

    const setMonthYear = (month, year) => {
        props.getCurrentMonth(month);
        props.getCurrentYear(year);
    }

    const createCalendar = (month, year) => {
        let firstDay = (new Date(year, month)).getDay();
        let date = 1;
        let cal = [];
        let todaysDate = 0;

        for (let i = 0; i < 6; i++) {
            for (let j = 0; j < 7; j++) {
                if (i === 0 && j < firstDay) {
                    cal.push(undefined);
                }
                else if (date > daysInMonth(month, year)) {
                    break;
                }
                else {
                    let today = new Date();

                    if (date === today.getDate() && year === today.getFullYear() && month === today.getMonth()) {
                        todaysDate = date;
                    }

                    cal.push(date);
                    date++;
                }
            }
        }

        return [cal, todaysDate];
    };

    const handleCellClick = (date) => {

        if (date) {
            if (props.onCellClick) {
                props.onCellClick(date);
            }
            setSelectedCell(date.getTime());
        }
    };


    const handleNextMonth = () => {
        if (month === 11) {
            setMonth(0);
            setYear(prevYear => prevYear + 1);
            props.onMonthChange(0, year + 1)
        } else {
            setMonth(prevMonth => prevMonth + 1);
            props.onMonthChange(month + 1, year)
        }
    };

    const handlePrevMonth = () => {
        if (month === 0) {
            setMonth(11);
            setYear(prevYear => prevYear - 1);
            props.onMonthChange(11, year - 1)
        } else {
            setMonth(prevMonth => prevMonth - 1);
            props.onMonthChange(month - 1, year)
        }
    };

    const handleTodayClick = () => {
        let date = new Date();
        date.setHours(0, 0, 0, 0);
        setMonth(getCurrentMonth());
        setYear(getCurrentYear());
        setMonthYear(getCurrentMonth(), getCurrentYear());
        handleDateChange(date);
        handleCellClick(date);
    };

    const dayNumbering = createCalendar(month, year);

    return (
        <div style={{
            width: '100%',
            minWidth: '90vw'
        }}>
            <Grid container alignItems='center'>
                <Box style={{
                    display: 'flex',
                    alignItems: 'center',
                }}>
                    <Button size="small" color="inherit" onClick={handleTodayClick}>
                        Today
                    </Button>
                    <Tooltip title="Previous" aria-label="previous">
                        <IconButton onClick={handlePrevMonth}><NavigateBeforeOutlined /></IconButton>
                    </Tooltip>
                    <Typography variant="subtitle1" component='span'>{monthLabels[month]}, {year}</Typography>
                    <Tooltip title="Next" aria-label="next">
                        <IconButton onClick={handleNextMonth}><NavigateNextOutlined /></IconButton>
                    </Tooltip>
                </Box>

            </Grid>
            <MonthView
                ref={calendarRef}
                month={month}
                year={year}
                dayNumbering={dayNumbering}
                selectedCell={selectedCell}
                onCellClick={handleCellClick}
                weekLabelSize={props.weekLabelSize}
                highlightDays={props.highlightDays}
                cellRenderer={props.cellRenderer}
            />
        </div>
    );
};

// Prop types and validations.
Calendar.propTypes = {
    width: PropTypes.oneOf(['lg', 'md', 'sm', 'xl', 'xs']).isRequired,
    month: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
    weekLabelSize: PropTypes.oneOf(['short', 'long'])
};

// Default prop values.
Calendar.defaultProps = {
    month: ((new Date()).getMonth()),
    year: ((new Date()).getFullYear()),
    weekLabelSize: 'short',
    selectedCell: undefined,
    showNextPrevious: false,
    itineraryView: false,
    showExcel: false,
    showCalendar: true,
    cellRenderer: () => { return <span>&nbsp;</span>; },
    onCellClick: () => { throw 'function onCellClick() is not handled.'; }
};

export default Calendar;